/**
 * SSOT języka komunikacji studio karuzel: role sekcji, CTA i „Teraz”.
 * Hierarchia: next > path > optional > info.
 */

import type { CarouselItem } from "@/lib/api";
import type { StudioTab } from "@/lib/carouselStudioMeta";

export type StudioSectionRole = "next" | "path" | "optional" | "info";
export type StudioCtaRole = "primary" | "secondary" | "quiet";
export type StudioTrack = "" | "operator" | "ai";

export type StudioNowAction = {
  label: string;
  detail: string;
  tab: StudioTab;
  /** Kotwica sekcji na bieżącej zakładce (id HTML), gdy tab === bieżąca */
  sectionId?: string;
};

export function ctaVariant(role: StudioCtaRole): "brand" | "outline" | "ghost" {
  if (role === "primary") return "brand";
  if (role === "secondary") return "outline";
  return "ghost";
}

function hasIdeas(item?: CarouselItem | null): boolean {
  return Boolean(item?.ideas?.length);
}

function hasSelectedIdea(item?: CarouselItem | null): boolean {
  return Boolean((item?.selected_idea_id || "").trim()) || Boolean(item?.ideas?.some((i) => i.selected));
}

function hasCopy(item?: CarouselItem | null): boolean {
  return Boolean(item?.copy?.slides?.length);
}

function copyApproved(item?: CarouselItem | null): boolean {
  return Boolean((item?.copy_approved_at || "").trim());
}

/** Gate Złóż: rewrite z niepustymi actions = najpierw Wdróż / popraw treść. */
export function copyRenderBlockedReason(item?: CarouselItem | null): string {
  const review = item?.discussion?.copy_review;
  if (!review || String(review.verdict || "").trim().toLowerCase() !== "rewrite") return "";
  const actions = review.actions || [];
  if (!actions.length) return "";
  return "najpierw wdróż poprawki copy albo popraw treść (werdykt rewrite)";
}

function hasRendered(item?: CarouselItem | null): boolean {
  return Boolean(item?.slides?.length);
}

function isPublished(item?: CarouselItem | null): boolean {
  const st = String(item?.status || "");
  return st === "published" || st === "scheduled" || Boolean(item?.ig_permalink || item?.fb_permalink);
}

/**
 * Jedyny obowiązkowy następny krok dla operatora (heurystyka stanu × tor × zakładka).
 */
export function nextActionFor(
  tab: StudioTab,
  track: StudioTrack,
  item: CarouselItem | null | undefined,
  extras?: { draftText?: string },
): StudioNowAction {
  const draft = String(extras?.draftText ?? item?.draft_text ?? "").trim();

  if (!track) {
    return {
      label: "Wybierz tor",
      detail: "Szkic własny albo pełne AI. Tory nie mieszają się na jednym briefie.",
      tab: "brief",
      sectionId: "studio-track",
    };
  }

  if (track === "operator") {
    if (!draft && !hasCopy(item)) {
      return {
        label: "Wklej szkic i zredaguj slajdy",
        detail: "Tor własny: tekst 1:1 na ściany, bez 4 koncepcji.",
        tab: "brief",
        sectionId: "studio-primary-cta",
      };
    }
    if (draft && !hasCopy(item)) {
      if (tab === "draft") {
        return {
          label: "Poczekaj na wynik albo uruchom ponownie",
          detail: "Śledzisz plan układu i kładzenie szkicu.",
          tab: "draft",
          sectionId: "studio-now",
        };
      }
      return {
        label: "Zredaguj slajdy",
        detail: "Z Wejścia startuje plan i redakcja ścian ze szkicu.",
        tab: "brief",
        sectionId: "studio-primary-cta",
      };
    }
    if (hasCopy(item) && !copyApproved(item)) {
      return {
        label: "Złóż obrazki",
        detail: "Copy jest gotowe. Na Wyjściu złóż PNG ze ścian.",
        tab: "render",
        sectionId: "studio-primary-cta",
      };
    }
    if (copyApproved(item) && !hasRendered(item)) {
      return {
        label: "Złóż obrazki",
        detail: "Wyjście składa PNG z zatwierdzonego copy.",
        tab: "render",
        sectionId: "studio-primary-cta",
      };
    }
    if (hasRendered(item) && !isPublished(item)) {
      return {
        label: "Zaplanuj albo publikuj",
        detail: "Slot wt/śr/pt albo Publikuj teraz.",
        tab: "publish",
        sectionId: "studio-primary-cta",
      };
    }
    const pendingDisc = (item?.lessons || []).filter(
      (l) => l.status === "pending" && (!l.source || l.source === "discussion"),
    ).length;
    if (pendingDisc > 0) {
      return {
        label: "Zatwierdź wnioski Retro",
        detail: "Dyskusja copy → globalna pamięć.",
        tab: "retro",
      };
    }
    return {
      label: "Gotowe",
      detail: "Karuzela w kolejce albo na kanale. Możesz zacząć nową.",
      tab: "publish",
    };
  }

  // track === "ai"
  if (tab === "draft") {
    return {
      label: "Przejdź do Przetwarzania",
      detail: "Zakładka Szkic jest pod tor własny. W AI pracujesz na koncepcjach.",
      tab: "copy",
    };
  }
  if (!hasIdeas(item)) {
    return {
      label: "Złóż 4 koncepcje",
      detail: "Brief i źródła → karty. Potem wybierasz jedną.",
      tab: "brief",
      sectionId: "studio-primary-cta",
    };
  }
  if (!hasSelectedIdea(item)) {
    return {
      label: "Wybierz kartę koncepcji",
      detail: "Klik w kartę tylko zaznacza. Potem Generuj treść.",
      tab: "copy",
      sectionId: "studio-concepts",
    };
  }
  if (!hasCopy(item)) {
    return {
      label: "Generuj treść",
      detail: "Role i redaktor na wybranej karcie.",
      tab: "copy",
      sectionId: "studio-primary-cta",
    };
  }
  if (!hasRendered(item)) {
    return {
      label: "Złóż obrazki",
      detail: "Copy jest. Wyjście składa PNG.",
      tab: "render",
      sectionId: "studio-primary-cta",
    };
  }
  if (!copyApproved(item)) {
    return {
      label: "Zatwierdź do publikacji",
      detail: "Po złożeniu PNG zatwierdź serię na Wyjściu.",
      tab: "render",
      sectionId: "studio-primary-cta",
    };
  }
  if (!isPublished(item)) {
    return {
      label: "Zaplanuj albo publikuj",
      detail: "Slot albo Publikuj teraz.",
      tab: "publish",
      sectionId: "studio-primary-cta",
    };
  }
  const pendingDisc = (item?.lessons || []).filter(
    (l) => l.status === "pending" && (!l.source || l.source === "discussion"),
  ).length;
  if (pendingDisc > 0 || tab === "retro") {
    return {
      label: "Zatwierdź wnioski Retro",
      detail: "Dyskusja copy → globalna pamięć.",
      tab: "retro",
    };
  }
  const pendingRes = (item?.lessons || []).filter((l) => l.status === "pending" && l.source === "results").length;
  if (pendingRes > 0 || tab === "wyniki") {
    return {
      label: "Zatwierdź wnioski z Wyników",
      detail: "Metryki → globalna pamięć.",
      tab: "wyniki",
    };
  }
  return {
    label: "Gotowe",
    detail: "Karuzela w kolejce albo na kanale. Retro i Wyniki uczysz ręcznie approve.",
    tab: "publish",
  };
}

export function trackLabel(track: StudioTrack): string {
  if (track === "operator") return "Tor: szkic";
  if (track === "ai") return "Tor: AI";
  return "Tor: nie wybrany";
}

export type PublicationKind = "" | "carousel" | "feed_post" | "czasotekst";

export function publicationKindLabel(kind: PublicationKind | string | undefined): string {
  if (kind === "carousel") return "Typ: Karuzela";
  if (kind === "feed_post") return "Typ: Post 1-grafika";
  if (kind === "czasotekst") return "Typ: Czasotekst";
  return "Typ: nie wybrany";
}

export function publicationKindShort(kind: PublicationKind | string | undefined): string {
  if (kind === "carousel") return "karuzela";
  if (kind === "feed_post") return "post";
  if (kind === "czasotekst") return "czasotekst";
  return "";
}
